import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { normalizeAuthEmail } from "@/lib/authEmail";

export const Route = createFileRoute("/admin-login")({
  head: () => ({
    meta: [
      { title: "Staff Sign In — Currency Thoughts" },
      {
        name: "description",
        content:
          "Restricted sign-in for Currency Thoughts administrators and Family Wealth Strategists.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Staff Sign In — Currency Thoughts" },
      {
        property: "og:description",
        content: "Restricted staff access to the Currency Thoughts command center.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/admin-login" }],
  }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const normalizedEmail = normalizeAuthEmail(email);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (signInError || !data.user) {
      setLoading(false);
      setError(
        "That email and password did not match a staff account. Use the reset link below if you have not set a password yet.",
      );
      return;
    }

    const uid = data.user.id;
    const { data: roleRows } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid);
    const roles = (roleRows ?? []).map((r) => r.role as string);

    const isAdmin = roles.includes("admin") || roles.includes("super_admin");
    const isAgent = roles.includes("agent");

    if (!isAdmin && !isAgent) {
      await supabase.from("activity_logs").insert({
        user_id: uid,
        action: "staff_login_denied",
        target_type: "auth",
        metadata: { roles },
      });
      await supabase.auth.signOut();
      setLoading(false);
      setError(
        "This account does not have staff access. Client accounts sign in through the client portal.",
      );
      return;
    }

    await supabase.from("activity_logs").insert({
      user_id: uid,
      action: "staff_login_success",
      target_type: "auth",
      metadata: { roles, entry: "admin-login" },
    });

    setLoading(false);
    toast.success(isAdmin ? "Welcome to the Command Center." : "Welcome back, Strategist.");
    navigate({ to: isAdmin ? "/admin" : "/agent" });
  };

  return (
    <AuthLayout eyebrow="Restricted Access" heading="Staff sign in">
      <form onSubmit={onSubmit} className="space-y-5">
        <p className="font-sans text-[13px] leading-relaxed text-charcoal-500">
          For administrators and Family Wealth Strategists only. Client households sign in
          through the{" "}
          <Link to="/login" className="font-semibold text-bronze-600 hover:text-bronze-400">
            client portal
          </Link>
          .
        </p>

        <StaffField
          id="admin-email"
          label="Staff email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputMode="email"
          required
        />
        <StaffField
          id="admin-password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            search={{ email: normalizeAuthEmail(email) }}
            className="font-sans text-[12px] text-bronze-600 hover:text-bronze-400"
          >
            Create or reset password
          </Link>
        </div>

        {error && (
          <div className="border border-bronze-600/30 bg-cream-50 p-4 font-sans text-[13px] leading-relaxed text-charcoal-500">
            {error}
          </div>
        )}

        <PrimaryButton type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? "Verifying…" : "Enter Command Center"}
        </PrimaryButton>

        <p className="text-center font-sans text-[11px] leading-relaxed text-charcoal-500">
          All staff sign-in attempts are recorded in the activity log.
        </p>
      </form>
    </AuthLayout>
  );
}

function StaffField({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  inputMode,
  required,
}: {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  inputMode?: "email";
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        inputMode={inputMode}
        required={required}
        className="mt-2 w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none"
        style={{ borderRadius: "var(--radius-button)" }}
      />
    </div>
  );
}
